<template>
  <UModal
    v-model:open="isOpen"
    :title="title"
    :description="description"
    :ui="{ content: 'sm:max-w-4xl' }"
  >
    <template #body>
      <div class="space-y-4">
        <div v-if="loading" class="flex flex-col items-center justify-center py-12 gap-3">
          <UIcon class="w-8 h-8 animate-spin text-primary-500" name="i-heroicons-arrow-path" />
          <p class="text-sm text-gray-500">Schema wird geladen...</p>
        </div>

        <div v-else class="flex flex-col md:grid md:grid-cols-2 gap-6 h-[500px]">
          <!-- Available Fields -->
          <div
            class="flex flex-col border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden min-h-0"
          >
            <div
              class="bg-gray-50 dark:bg-gray-900 px-3 py-2 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center shrink-0"
            >
              <span class="text-xs font-bold uppercase tracking-wider text-gray-500"
                >Verfügbare Felder</span
              >
            </div>
            <div class="px-2 pt-2 shrink-0">
              <UInput
                ref="availableSearchInputRef"
                v-model="availableSearch"
                icon="i-heroicons-magnifying-glass"
                placeholder="Felder durchsuchen..."
                color="primary"
                variant="outline"
                size="sm"
                class="w-full"
                :ui="{
                  trailing: 'pe-1',
                }"
              >
                <template #trailing>
                  <UButton
                    v-if="availableSearch"
                    color="neutral"
                    variant="ghost"
                    icon="i-heroicons-x-mark"
                    size="xs"
                    @click="availableSearch = ''"
                  />
                </template>
              </UInput>
            </div>
            <div class="flex-1 overflow-y-auto p-2 space-y-1">
              <div
                v-for="col in filteredAvailableColumns"
                :key="col.key"
                class="flex items-center justify-between p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded group transition-colors cursor-pointer"
                @click="addColumn(col)"
              >
                <span :title="col.label" class="text-sm truncate">{{ col.label || col.key }}</span>
                <UButton
                  color="primary"
                  icon="i-heroicons-plus-circle"
                  size="xs"
                  variant="ghost"
                  class="opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </div>
              <div
                v-if="filteredAvailableColumns.length === 0 && availableColumns.length > 0"
                class="flex flex-col items-center justify-center py-8 text-center text-sm text-gray-500"
              >
                Keine Treffer für „{{ availableSearch }}“
              </div>
            </div>
          </div>

          <!-- Selected Columns -->
          <div
            class="flex flex-col border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden min-h-0"
          >
            <div
              class="bg-gray-50 dark:bg-gray-900 px-3 py-2 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center shrink-0"
            >
              <span class="text-xs font-bold uppercase tracking-wider text-gray-500"
                >Ausgewählte Spalten</span
              >
              <span
                class="text-[10px] bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 px-1.5 py-0.5 rounded-full font-bold"
              >
                {{ selectedColumns.length }}
              </span>
            </div>
            <div class="px-2 pt-2 shrink-0">
              <UInput
                v-model="selectedSearch"
                icon="i-heroicons-magnifying-glass"
                placeholder="Spalten durchsuchen..."
                color="primary"
                variant="outline"
                size="sm"
                class="w-full"
                :ui="{
                  trailing: 'pe-1',
                }"
              >
                <template #trailing>
                  <UButton
                    v-if="selectedSearch"
                    color="neutral"
                    variant="ghost"
                    icon="i-heroicons-x-mark"
                    size="xs"
                    @click="selectedSearch = ''"
                  />
                </template>
              </UInput>
            </div>
            <div class="flex-1 overflow-y-auto p-2 space-y-1">
              <div
                v-for="col in filteredSelectedColumns"
                :key="col.key"
                :class="[
                  col.locked
                    ? 'ring-1 ring-primary-500 border-primary-500 bg-gray-50 dark:bg-gray-900'
                    : 'bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm',
                ]"
                class="group flex items-center gap-2 p-2 rounded"
              >
                <div v-if="!col.locked" class="flex flex-col gap-0.5 shrink-0">
                  <UButton
                    :disabled="realIndex(col) <= firstUnlockedIndex"
                    class="p-0 h-4 min-h-0"
                    color="neutral"
                    icon="i-heroicons-chevron-up"
                    size="xs"
                    variant="ghost"
                    @click.stop="moveUp(col)"
                  />
                  <UButton
                    :disabled="realIndex(col) === selectedColumns.length - 1"
                    class="p-0 h-4 min-h-0"
                    color="neutral"
                    icon="i-heroicons-chevron-down"
                    size="xs"
                    variant="ghost"
                    @click.stop="moveDown(col)"
                  />
                </div>
                <UIcon v-else class="w-4 h-4 text-primary-500 shrink-0" name="i-heroicons-lock-closed" />

                <span
                  :class="{ 'text-primary-600 dark:text-primary-400 font-semibold': col.locked }"
                  :title="col.label"
                  class="text-sm flex-1 truncate font-medium"
                >
                  {{ col.label || col.key }}
                </span>

                <UButton
                  v-if="!col.locked"
                  color="error"
                  icon="i-heroicons-minus-circle"
                  size="xs"
                  variant="ghost"
                  class="shrink-0"
                  @click.stop="removeColumn(col)"
                />
              </div>
              <div
                v-if="filteredSelectedColumns.length === 0 && selectedColumns.length > 0"
                class="flex flex-col items-center justify-center py-8 text-center text-sm text-gray-500"
              >
                Keine Treffer für „{{ selectedSearch }}“
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex flex-col sm:flex-row justify-between w-full items-center gap-4">
        <UButton
          color="neutral"
          label="Auf Standard zurücksetzen"
          variant="outline"
          class="w-full sm:w-auto"
          @click="reset"
        />
        <div class="flex gap-2 w-full sm:w-auto">
          <UButton color="neutral" variant="ghost" class="flex-1 sm:flex-none" @click="isOpen = false">Abbrechen</UButton>
          <UButton color="primary" label="Speichern" class="flex-1 sm:flex-none" @click="save" />
        </div>
      </div>
    </template>
  </UModal>
</template>

<script lang="ts" setup>
import { ref, watch, computed, nextTick, useTemplateRef } from 'vue'

interface Column {
  key: string
  label: string
  locked?: boolean
}

const props = withDefaults(
  defineProps<{
    allColumns: Column[]
    initialSelected: Column[]
    loading?: boolean
    title?: string
    description?: string
  }>(),
  {
    loading: false,
    title: 'Spalten konfigurieren',
    description: 'Wählen Sie die Spalten aus, die in der Tabelle angezeigt werden sollen.',
  },
)

const emit = defineEmits(['save', 'reset'])

const isOpen = ref(false)
const selectedColumns = ref<Column[]>([])
const availableColumns = ref<Column[]>([])
const availableSearch = ref('')
const selectedSearch = ref('')
const availableSearchInputRef = useTemplateRef<{ inputRef: HTMLInputElement | null }>(
  'availableSearchInputRef',
)

const firstUnlockedIndex = computed(() => {
  const idx = selectedColumns.value.findIndex((c) => !c.locked)
  return idx === -1 ? 0 : idx
})

function matchesSearch(col: Column, term: string) {
  const t = term.trim().toLowerCase()
  if (!t) return true
  return (col.label || '').toLowerCase().includes(t) || col.key.toLowerCase().includes(t)
}

const filteredAvailableColumns = computed(() =>
  availableColumns.value.filter((c) => matchesSearch(c, availableSearch.value)),
)

const filteredSelectedColumns = computed(() =>
  selectedColumns.value.filter((c) => matchesSearch(c, selectedSearch.value)),
)

watch(
  () => props.initialSelected,
  (newVal) => {
    selectedColumns.value = normalizeColumns(newVal)
  },
  { immediate: true, deep: true },
)

watch(
  () => props.allColumns,
  () => {
    updateAvailable()
  },
  { immediate: true, deep: true },
)

watch(
  selectedColumns,
  () => {
    updateAvailable()
  },
  { deep: true },
)

function updateAvailable() {
  const selectedKeys = new Set(selectedColumns.value.map((c) => c.key))
  availableColumns.value = normalizeColumns(props.allColumns).filter(
    (c) => !selectedKeys.has(c.key),
  )
}

function normalizeColumns(columns: Column[]) {
  const keys = new Set<string>()
  return columns.reduce<Column[]>((normalized, column) => {
    const key = column.key.trim()
    if (!key || keys.has(key)) return normalized

    keys.add(key)
    normalized.push({ ...column, key, label: column.label || key })
    return normalized
  }, [])
}

function realIndex(col: Column) {
  return selectedColumns.value.findIndex((c) => c.key === col.key)
}

function addColumn(col?: Column) {
  const key = col?.key.trim()
  if (!col || !key || selectedColumns.value.some((c) => c.key === key)) {
    return
  }
  selectedColumns.value.push({ ...col, key, label: col.label || key })
}

function removeColumn(col: Column) {
  const index = selectedColumns.value.findIndex((c) => c.key === col.key)
  if (index === -1 || selectedColumns.value[index].locked) return
  selectedColumns.value.splice(index, 1)
}

function moveUp(col: Column) {
  const index = selectedColumns.value.findIndex((c) => c.key === col.key)
  if (index === -1 || index <= firstUnlockedIndex.value) return
  const prev = selectedColumns.value[index - 1]
  const curr = selectedColumns.value[index]
  if (prev && curr && !prev.locked) {
    selectedColumns.value[index] = prev
    selectedColumns.value[index - 1] = curr
  }
}

function moveDown(col: Column) {
  const index = selectedColumns.value.findIndex((c) => c.key === col.key)
  if (index === -1 || index >= selectedColumns.value.length - 1) return
  if (selectedColumns.value[index].locked) return
  const next = selectedColumns.value[index + 1]
  const curr = selectedColumns.value[index]
  if (next && curr) {
    selectedColumns.value[index] = next
    selectedColumns.value[index + 1] = curr
  }
}

function save() {
  selectedColumns.value = normalizeColumns(selectedColumns.value)
  emit('save', [...selectedColumns.value])
  isOpen.value = false
}

function reset() {
  emit('reset')
}

defineExpose({
  open: () => {
    availableSearch.value = ''
    selectedSearch.value = ''
    isOpen.value = true
    nextTick(() => {
      availableSearchInputRef.value?.inputRef?.focus()
    })
  },
  close: () => (isOpen.value = false),
})
</script>
